import OpenXIcon from '../assets/images/openx_icon.png';
import PubmaticIcon from '../assets/images/pubmatic_icon.ico';
import TTDIcon from '../assets/images/ttd_icon.png';
import axios from 'axios';
import {
  AUDIENCE_EXPORT_TYPES,
  ENUM_REGEX,
  ENUM_SYSTEM_USED,
  EXPERIAN_MAX_CHILD_SEGMENT_NAME_LENGTH,
  MAX_SEGMENT_DESCRIPTION_LENGTH,
  MAX_SEGMENT_NAME_LENGTH,
  OPENX_MAX_SEGMENT_NAME_LENGTH,
  TTD_DATA_ALLIANCE_MAX_SEGMENT_NAME_LENGTH,
  ValidationErrorMessages,
  YAHOO_DSP_CPM_MAX_VALUE,
  YAHOO_DSP_MAX_SEGMENT_NAME_LENGTH,
  TIKTOK_MAX_SEGMENT_NAME_LENGTH,
  doesNotUseIs,
} from '../constants';
import { AUDIENCE_STUDIO_API_ENDPOINT } from '../pages/audienceManager/variables/index';
import { CSV_FILE_FORMAT_ERROR, VALID_FILE } from '../content';
import { auth } from 'utils/firebase';

/**
 * Get the epoch time (milliseconds since January 1, 1970, 00:00:00 UTC) for a given date.
 * @param {Date | string | number} date The date for which to calculate the epoch time.
 * @param {number} [hours=0] The hours component of the time (0-23).
 * @param {number} [minutes=0] The minutes component of the time (0-59).
 * @param {number} [seconds=0] The seconds component of the time (0-59).
 * @param {number} [milliseconds=0] The milliseconds component of the time (0-999).
 * @returns {number} The epoch time in milliseconds.
 */
export const getEpochTime = (
  date: Date | string | number,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0
): number => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  // Set hours, minutes, seconds, and milliseconds to provided values
  date.setUTCHours(hours, minutes, seconds, milliseconds);

  // Calculate epoch time
  return date.getTime();
};

/**
 * Handles onKeyDown event for decimal input fields.
 * Prevents non-numeric characters (except decimal point) from being entered.
 * 
 * @param {React.KeyboardEvent} e - The keyboard event
 * @param {string} currentValue - The current value of the input field
 * @param {ENUM_REGEX} regex - The regex pattern to validate against (e.g., ENUM_REGEX.DECIMAL)
 * @returns {void}
 */
export const handleDecimalKeyDown = (
  e: React.KeyboardEvent,
  currentValue: string,
  regex: ENUM_REGEX = ENUM_REGEX.DECIMAL
): void => {
  // Allow paste operations: Control+V (Windows/Linux) and Cmd+V (Mac)
  if (
    (e.ctrlKey && e.key === 'v') ||
    (e.metaKey && e.key === 'v')
  ) {
    return;
  }

  // Prevent spaces for decimal fields
  if (
    (e.key === ' ' ||
      e.key === 'Space' ||
      e.keyCode === 32 ||
      e.code === 'Space')
  ) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  try {
    const regexObj = new RegExp(regex);
    if (
      e.key.length === 1 &&
      !regexObj.test(currentValue + e.key) &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  } catch (err) {
    console.error('Invalid regex pattern:', regex, err);
  }
};

/**
 * Handles onInput event for decimal input fields.
 * Sanitizes the input to only allow valid decimal values.
 * 
 * @param {React.FormEvent} e - The input event
 * @param {string} currentValue - The current value of the input field
 * @param {(value: string) => void} onChange - Callback function to update the value
 * @param {ENUM_REGEX} regex - The regex pattern to validate against (e.g., ENUM_REGEX.DECIMAL)
 * @returns {void}
 */
export const handleDecimalInput = (
  e: React.FormEvent,
  currentValue: string,
  onChange: (value: string) => void,
  regex: ENUM_REGEX = ENUM_REGEX.DECIMAL
): void => {
  const target = e.target as HTMLInputElement;
  const inputValue = target.value;

  // Revert to previous value if spaces are detected
  if (inputValue.includes(' ')) {
    target.value = currentValue;
    onChange(currentValue);
    return;
  }

  // Sanitize input for decimal fields
  if (regex === ENUM_REGEX.DECIMAL) {
    let sanitizedValue = inputValue.replace(/\s/g, '');
    const parts = sanitizedValue.split('.');
    if (parts.length > 2) {
      sanitizedValue = parts[0] + '.' + parts.slice(1).join('');
    }
    sanitizedValue = sanitizedValue.replace(/[^\d.]/g, '');
    if (inputValue !== sanitizedValue) {
      target.value = sanitizedValue;
      onChange(sanitizedValue);
    }
  }
  // Sanitize input for whole number fields
  else if (regex === ENUM_REGEX.WHOLE_NUMBER) {
    const sanitizedValue = inputValue.replace(/[^\d]/g, '');
    if (inputValue !== sanitizedValue) {
      target.value = sanitizedValue;
      onChange(sanitizedValue);
    }
  }
};

/**
 * Handles onBeforeInput event for decimal/whole number fields.
 * Prevents spaces from being entered.
 * 
 * @param {React.FormEvent} e - The before input event
 * @param {ENUM_REGEX} regex - The regex pattern to validate against
 * @returns {void}
 */
export const handleDecimalBeforeInput = (
  e: React.FormEvent,
  regex?: ENUM_REGEX
): void => {
  if (
    regex &&
    [ENUM_REGEX.DECIMAL, ENUM_REGEX.WHOLE_NUMBER].includes(regex)
  ) {
    const inputData = (e as any).data;
    if (inputData && inputData.includes(' ')) {
      e.preventDefault();
    }
  }
};

export const isFloat = (input: string) => {
  if (input === '') return false;
  // Regular expression to match float numbers (requires at least one digit)
  const floatRegex = /^-?(\d+\.?\d*|\d*\.\d+)$/;
  // Check if the input matches the float regex
  return floatRegex.test(input);
};

export const validateCustomAudienceCsv = (
  file: File,
  setValidationDetails: any
) => {
  const reader = new window.FileReader();

  reader.onload = (e) => {
    const contents = e?.target?.result;
    const lines = contents?.split('\n');

    // Check if CSV has only one column and all values are alphanumeric
    const isValid = lines.every((line) => {
      const values = line.trim().split(',');
      if (
        values.length === 1 &&
        ((values[0].match(/^[a-zA-Z0-9]+$/) && values[0].length === 40) ||
          values[0] === '')
      ) {
        return true;
      }
      return false;
    });

    if (isValid) {
      setValidationDetails({
        valid: true,
        message: VALID_FILE,
      });
    } else {
      setValidationDetails({
        valid: false,
        message: CSV_FILE_FORMAT_ERROR,
      });
    }
  };

  reader.readAsText(file);
};

/**
 * Retrieves audience emails.
 *
 * @param {boolean} isCustomAudience - Indicates if the audience is custom audience.
 * @returns {Promise<AxiosResponse>} A promise that resolves to the Axios response containing the audience emails.
 */
export const getAudeinceEmails = (isCustomAudience: boolean) => {
  const url = `${AUDIENCE_STUDIO_API_ENDPOINT}/emails`;
  return auth?.currentUser?.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.get(url);
  });
};

/**
 * Retrieves all modeled audiences.
 *
 * @returns {Promise<AxiosResponse>} A promise that resolves to the Axios response containing the all modeled audiences.
 */
export const getModeledAudeinces = () => {
  const url = `${AUDIENCE_STUDIO_API_ENDPOINT}/audiences/modeled`;
  return auth?.currentUser?.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.get(url);
  });
};

/**
 * Retrieves all modeled audience counts.
 *
 * @returns {Promise<AxiosResponse>} A promise that resolves to the Axios response containing the modeled audience counts.
 */
export const getModeledAudeinceCounts = (payload: any) => {
  const url = `${AUDIENCE_STUDIO_API_ENDPOINT}/audiences/modeledAudienceCount`;
  return auth?.currentUser?.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.post(url, payload);
  });
};

/**
 * Validates the segment name based on the selected export type.
 *
 * @param {string} selectedExportType - The type of export selected (e.g., 'custom_segment', 'ots_segment').
 * @param {string} segmentName - The name of the segment to be validated.
 * @returns {{ isValid: boolean, validationMessage: string }} - An object indicating whether the segment name is valid and a corresponding validation message.
 */
export const isSegmentNameValid = (
  selectedExportType: Array<string> = [],
  segmentName: string
) => {
  const returnObject = {
    isValid: true,
    validationMessage: '',
  };

  // Regular expression to detect forbidden characters (double quotes, single quote, caret)
  const forbiddenCharsPattern = /['"^]/;
  const experianForbiddenCharsPattern = /["\\]|\/{2}|[^\x00-\x7F]/;
  // Regular expression to validate A- pattern (A- followed by exactly 7 digits, then any characters allowed)
  const metaSegmentNameRegexPattern = /^.*A-\d{7}(?!\d).*$/;

  // Check for A- pattern validation only for Meta custom seed audience export
  if (
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE
    ) &&
    segmentName &&
    !metaSegmentNameRegexPattern.test(segmentName)
  ) {
    returnObject.isValid = false;
    returnObject.validationMessage =
      ValidationErrorMessages.SEGMENT_NAME_A_PATTERN_ERROR;
    return returnObject;
  }

  if (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT)) {
    // Check for forbidden characters
    if (experianForbiddenCharsPattern.test(segmentName)) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.EXPERIAN_FORBIDDEN_CHARACTER_ERROR;
      return returnObject;
    }

    // Split the segment name by '>' and check the length of each part
    const segments = segmentName.split('>');

    for (const segment of segments) {
      if (segment.trim().length > EXPERIAN_MAX_CHILD_SEGMENT_NAME_LENGTH) {
        returnObject.isValid = false;
        returnObject.validationMessage =
          ValidationErrorMessages.EXPERIAN_SEGMENT_SECTION_TOO_LONG_ERROR;
        return returnObject;
      }
    }
  }
  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.OTS_SEGMENT) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
    ) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT)
  ) {
    // Check for forbidden characters
    if (forbiddenCharsPattern.test(segmentName)) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.FORBIDDEN_CHARACTER_ERROR;
      return returnObject;
    }

    if (segmentName && !segmentName.startsWith('Attain >')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.SEGMENT_NAME_START_CHAR_ERROR;
      return returnObject;
    }

    // Check for consecutive '>' symbols
    if (segmentName.replace(/\s/g, '').includes('>>')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.CONSECUTIVE_GREATER_THAN_SYMBOL_ERROR;
      return returnObject;
    }

    // Split the segment name by '>' and check the length of each part
    const segments = segmentName.split('>');

    for (const segment of segments) {
      if (segment.trim().length > MAX_SEGMENT_NAME_LENGTH) {
        returnObject.isValid = false;
        returnObject.validationMessage =
          ValidationErrorMessages.SEGMENT_SECTION_TOO_LONG_ERROR;
        return returnObject;
      }
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT)
  ) {
    if (segmentName.trim().length > MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.SEGMENT_SECTION_SHOULD_NOT_EXCEED_ERROR.replace(
          '${MAX_SEGMENT_NAME_LENGTH}',
          MAX_SEGMENT_NAME_LENGTH.toString()
        );
      return returnObject;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT)
  ) {
    if (segmentName.trim().length > OPENX_MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.SEGMENT_SECTION_SHOULD_NOT_EXCEED_ERROR.replace(
          '${MAX_SEGMENT_NAME_LENGTH}',
          OPENX_MAX_SEGMENT_NAME_LENGTH.toString()
        );
      return returnObject;
    }
  }

  if (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.TTD_DATA_ALLIANCE)) {
    // Check for forbidden characters
    if (forbiddenCharsPattern.test(segmentName)) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.FORBIDDEN_CHARACTER_ERROR;
      return returnObject;
    }

    if (segmentName && !segmentName.startsWith('Data Alliance >')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.TTD_DATA_ALLIANCE_SEGMENT_NAME_START_CHAR_ERROR;
      return returnObject;
    }

    // Check for consecutive '>' symbols
    if (segmentName.replace(/\s/g, '').includes('>>')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.CONSECUTIVE_GREATER_THAN_SYMBOL_ERROR;
      return returnObject;
    }

    if (segmentName.trim().length > TTD_DATA_ALLIANCE_MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.SEGMENT_SECTION_SHOULD_NOT_EXCEED_ERROR.replace(
          '${MAX_SEGMENT_NAME_LENGTH}',
          TTD_DATA_ALLIANCE_MAX_SEGMENT_NAME_LENGTH.toString()
        );
      return returnObject;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT)
  ) {
    if (segmentName.trim().length > YAHOO_DSP_MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.YAHOO_DSP_SEGMENT_NAME_LENGTH_ERROR;
      return returnObject;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE
    )
  ) {
    if (segmentName.trim().length > TIKTOK_MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.TIKTOK_SEGMENT_NAME_LENGTH_ERROR;
      return returnObject;
    }
  }
  return returnObject;
};

export const isCPMValid = (
  selectedExportType: Array<string> = [],
  cpm: string
) => {
  const returnObject = {
    isValid: true,
    cpmErrorMessage: '',
  };

  // LiveRamp validation: CPM must be >= 0.01
  const isLiveRampType =
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE);

  if (isLiveRampType) {
    if (cpm !== '' && isFloat(cpm)) {
      const cpmValue = parseFloat(cpm);
      if (cpmValue <= 0 || cpmValue < 0.01) {
        returnObject.isValid = false;
        returnObject.cpmErrorMessage =
          ValidationErrorMessages.LIVERAMP_CPM_MIN_ERROR;
      }
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT)
  ) {
    if (!isFloat(cpm)) {
      returnObject.isValid = false;
      returnObject.cpmErrorMessage =
        ValidationErrorMessages.YAHOO_DSP_CPM_RANGE_ERROR;
    }

    if (
      (cpm !== '' && parseFloat(cpm) < 0) ||
      parseFloat(cpm) > YAHOO_DSP_CPM_MAX_VALUE
    ) {
      returnObject.isValid = false;
      returnObject.cpmErrorMessage =
        ValidationErrorMessages.YAHOO_DSP_CPM_RANGE_ERROR;
    }
  }
  return returnObject;
};

/**
 * Validates Programmatic % of media value for LiveRamp exports.
 * Must be greater than zero (>= 0.01).
 *
 * @param {Array<string>} selectedExportType - Array of selected export types
 * @param {string} percentageOfMedia - The percentage of media value to validate
 * @returns {{ isValid: boolean, percentageOfMediaErrorMessage: string }} - An object indicating whether the percentage of media is valid and a corresponding validation message.
 */
export const isPercentageOfMediaValid = (
  selectedExportType: Array<string> = [],
  percentageOfMedia: string
) => {
  const returnObject = {
    isValid: true,
    percentageOfMediaErrorMessage: '',
  };

  const isLiveRampType =
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE);

  if (isLiveRampType && percentageOfMedia !== '') {
    if (!isFloat(percentageOfMedia)) {
      returnObject.isValid = false;
      returnObject.percentageOfMediaErrorMessage =
        ValidationErrorMessages.LIVERAMP_PERCENTAGE_OF_MEDIA_MIN_ERROR;
    } else {
      const pomValue = parseFloat(percentageOfMedia);
      if (pomValue <= 0 || pomValue < 0.01) {
        returnObject.isValid = false;
        returnObject.percentageOfMediaErrorMessage =
          ValidationErrorMessages.LIVERAMP_PERCENTAGE_OF_MEDIA_MIN_ERROR;
      }
    }
  }

  return returnObject;
};

/**
 * Validates Advertiser Direct % of Media value for LiveRamp exports.
 * Must be greater than zero (>= 0.01).
 *
 * @param {Array<string>} selectedExportType - Array of selected export types
 * @param {string} advertiserDirect - The advertiser direct value to validate
 * @returns {{ isValid: boolean, advertiserDirectErrorMessage: string }} - An object indicating whether the advertiser direct is valid and a corresponding validation message.
 */
export const isAdvertiserDirectValid = (
  selectedExportType: Array<string> = [],
  advertiserDirect: string
) => {
  const returnObject = {
    isValid: true,
    advertiserDirectErrorMessage: '',
  };

  const isLiveRampType =
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE);

  if (isLiveRampType && advertiserDirect !== '') {
    if (!isFloat(advertiserDirect)) {
      returnObject.isValid = false;
      returnObject.advertiserDirectErrorMessage =
        ValidationErrorMessages.LIVERAMP_ADVERTISER_DIRECT_MIN_ERROR;
    } else {
      const adValue = parseFloat(advertiserDirect);
      if (adValue <= 0 || adValue < 0.01) {
        returnObject.isValid = false;
        returnObject.advertiserDirectErrorMessage =
          ValidationErrorMessages.LIVERAMP_ADVERTISER_DIRECT_MIN_ERROR;
      }
    }
  }

  return returnObject;
};

/**
 * Validates TV CPM value for Yahoo DSP exports.
 * TV CPM must be a number between 0 and 50.
 *
 * @param {string} tvCpm - The TV CPM value to validate.
 * @returns {{ isValid: boolean, tvCpmErrorMessage: string }} - An object indicating whether the TV CPM is valid and a corresponding validation message.
 */
export const isTvCpmValid = (
  selectedExportType: Array<string> = [],
  tvCpm: string
) => {
  const returnObject = {
    isValid: true,
    tvCpmErrorMessage: '',
  };

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT)
  ) {
    if (tvCpm === '' || !isFloat(tvCpm)) {
      returnObject.isValid = false;
      returnObject.tvCpmErrorMessage =
        ValidationErrorMessages.YAHOO_DSP_TV_CPM_RANGE_ERROR;
      return returnObject;
    }

    const tvCpmValue = parseFloat(tvCpm);
    if (tvCpmValue < 0 || tvCpmValue > YAHOO_DSP_CPM_MAX_VALUE) {
      returnObject.isValid = false;
      returnObject.tvCpmErrorMessage =
        ValidationErrorMessages.YAHOO_DSP_TV_CPM_RANGE_ERROR;
      return returnObject;
    }
  }
  return returnObject;
};

/**
 * Returns the default operator for a given property name.
 *
 * Some property names use the '=' operator by default, while others use 'is'.
 * This function checks if the property name is in the list that uses '=' and
 * returns the appropriate default operator.
 *
 * @param {string} propertyName - The name of the property to check.
 * @returns {string} - The default operator for the given property ('=' or 'is').
 */
export const getDefaultOperator = (propertyName: string): string => {
  const defaultOperator = doesNotUseIs[propertyName] ? '=' : 'is';
  return defaultOperator;
};

export const sortTableData = (data: any, sortBy: string, sortOrder: string) => {
  return data.sort((a, b) => {
    let fieldA = a[sortBy];
    let fieldB = b[sortBy];
    const isNumber = !isNaN(Number(fieldA));
    // If the field is a string, compare them lexicographically
    if (!isNumber) {
      fieldA = fieldA?.toLowerCase();
      fieldB = fieldB?.toLowerCase();
    }

    // If sortOrder is ascending (ASC)
    if (sortOrder === 'ASC') {
      if (fieldA > fieldB) return 1;
      if (fieldA < fieldB) return -1;
      return 0;
    }
    // If sortOrder is descending (DESC)
    else if (sortOrder === 'DESC') {
      if (fieldA > fieldB) return -1;
      if (fieldA < fieldB) return 1;
      return 0;
    }

    return 0; // In case sortOrder is neither ASC nor DESC
  });
};

export const groupModeledAudienceCountByIdType = (data: any) => {
  if (!Array.isArray(data)) {
    return {};
  }
  const sortedData = [...data].sort((a, b) => a.idType.localeCompare(b.idType));

  return sortedData.reduce((acc: any, item: any) => {
    if (!acc[item.idType]) {
      acc[item.idType] = [];
    }
    acc[item.idType].push(item);
    return acc;
  }, {});
};

export const calculateMinAndMaxAudienceCount = (groupedData: any) => {
  let minAudienceCount = 0;
  let maxAudienceCount = 0;
  Object.values(groupedData).forEach((items) => {
    if (items[0]) {
      minAudienceCount += items[0].audienceCount;
    }
    if (items[10]) {
      maxAudienceCount += items[10].audienceCount;
    }
  });

  return {
    minimumAudienceCount: minAudienceCount.toLocaleString(),
    maximumAudienceCount: maxAudienceCount.toLocaleString(),
  };
};

/**
 * Check desciption props & validation
 */
export const isDescriptionValid = (
  selectedExportType: Array<string> = [],
  description: string
) => {
  const result = {
    isValid: true,
    isRequired: false,
    validationMessage: '',
  };

  // Check if Meta custom seed audience is selected - description is required for Meta exports only
  if (
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE
    )
  ) {
    result.isRequired = true;

    if (!description.trim()) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_REQUIRED_ERROR;
      return result;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT)
  ) {
    result.isRequired = true;

    if (!description.trim()) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_REQUIRED_ERROR;
      return result;
    }

    if (description.trim().length > MAX_SEGMENT_NAME_LENGTH) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_SECTION_TOO_LONG_ERROR;
    }

    return result;
  }

  if (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT)) {
    const experianForbiddenCharsPattern = /["\\]|\/{2}|[^\x00-\x7F]/;
    result.isRequired = true;

    if (!description.trim()) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_REQUIRED_ERROR;
      return result;
    }

    // Check for forbidden characters
    if (experianForbiddenCharsPattern.test(description)) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.EXPERIAN_DESCRIPTION_NON_ASCII_CHARACTER_ERROR;
      return result;
    }

    if (description.trim().length > MAX_SEGMENT_DESCRIPTION_LENGTH) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.EXPERIAN_DESCRIPTION_TOO_LONG_ERROR;
    }

    return result;
  }

  return result;
};

export const generateOtsExportOptions = (audienceType: string) => {
  const otsOptions =
    audienceType === ENUM_SYSTEM_USED.COMBINED_AUDIENCE
      ? [
          { id: 'all', label: 'Toggle All' },
          {
            id: AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
            label: 'The Trade Desk',
            icon: TTDIcon,
          },
        ]
      : [
          { id: 'all', label: 'Toggle All' },
          {
            id: AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
            label: 'The Trade Desk',
            icon: TTDIcon,
          },
          {
            id: AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT,
            label: 'Pubmatic',
            icon: PubmaticIcon,
          },
          {
            id: AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT,
            label: 'OpenX',
            icon: OpenXIcon,
          },
        ];
  return otsOptions;
};

export const generatCustomExportOptions = (audienceType: string) => {
  const customOptions =
    audienceType === ENUM_SYSTEM_USED.COMBINED_AUDIENCE
      ? [
          {
            id: AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
            label: 'The Trade Desk Custom',
            icon: TTDIcon,
          },
        ]
      : [
          {
            id: AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
            label: 'The Trade Desk Custom',
            icon: TTDIcon,
          },
          {
            id: AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT,
            label: 'Pubmatic',
            icon: PubmaticIcon,
          },
          {
            id: AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT,
            label: 'OpenX',
            icon: OpenXIcon,
          },
        ];
  return customOptions;
};

export const sortRowsDescending = (rows: any) => {
  function extractNumberForSort(str: any) {
    if (!isNaN(str)) return parseInt(str, 10); // Entire string numeric
    const match = str.match(/(\d+)$/); // Numeric suffix for alphanumerics
    return match ? parseInt(match[1], 10) : NaN;
  }

  return rows.sort((a: any, b: any) => {
    const numA = extractNumberForSort(a.row);
    const numB = extractNumberForSort(b.row);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numB - numA; // Descending numeric order
    } else {
      return b.row.localeCompare(a.row); // Fallback to reverse lexicographic
    }
  });
};
